import React from 'react';
import { Car } from 'lucide-react';
import { cn } from '@/lib/utils';
import { RideType } from '@/types/ride';
import { getPriceEstimate, formatPrice } from '@parleyride/domain';

interface RideTypeSelectorProps {
  rideType: RideType;
  setRideType: (type: RideType) => void;
  /** Route length in km, once the endpoints are known. */
  distanceKm?: number | null;
  /** Route duration in minutes, once the endpoints are known. */
  durationMin?: number | null;
}

interface RideOption {
  type: RideType;
  description: string;
  /** The estimate as a number — this is what becomes estimated_market_rate. */
  amount: number;
  time: string;
}

const RideTypeSelector: React.FC<RideTypeSelectorProps> = ({
  rideType,
  setRideType,
  distanceKm,
  durationMin,
}) => {
  // Prices come from the pricing engine, not a table copied beside it.
  // These were hardcoded as '$12-15' / '$16-20' / '$25-30' / '$18-22' — a copy
  // of getPriceEstimate's *fallback* ranges, which meant this step quoted the
  // same figure for a two-mile hop and a cross-town run, and then disagreed
  // with the estimate the next step derived from the real route.
  const descriptions: Record<string, string> = {
    economy: 'Budget-friendly option',
    comfort: 'More space, comfy ride',
    premium: 'Luxury vehicles, top service',
    electric: 'Eco-friendly electric cars',
  };

  const rideOptions: RideOption[] = (['economy', 'comfort', 'premium', 'electric'] as const).map(
    (type) => {
      const { amount, time } = getPriceEstimate(type, distanceKm, durationMin);
      return { type, description: descriptions[type], amount, time };
    },
  );

  // Whether these figures describe *this* trip or are generic per-class ones.
  // The vehicle step is reachable without a route (routing can fail, or the
  // endpoints can be too far apart), and a generic number must not be
  // presented as an estimate for a journey.
  const isRouteBased = getPriceEstimate('economy', distanceKm, durationMin).isRouteBased;

  return (
    <div className="space-y-3">
      {/*
        This is the first price a rider sees, and the figure for whichever
        option they pick is stored as the ride's estimated_market_rate — the
        same number the negotiation screen later measures offers against.
        Naming it here is what makes that later comparison legible instead of
        arriving from nowhere.
      */}
      <p className="text-sm text-muted-foreground" data-testid="ride-type-estimate-note">
        {isRouteBased
          ? 'Estimated fare for this trip. Drivers negotiate up or down from here — you agree the final price together.'
          : 'Typical fare for each vehicle type. We could not measure your route, so this is not specific to your trip yet.'}
      </p>

      <div className="grid gap-3">
        {rideOptions.map((option) => (
          <div
            key={option.type}
            className={cn(
              'flex items-center justify-between p-3 border rounded-lg cursor-pointer transition-all',
              rideType === option.type ? 'border-ride-primary bg-accent/50' : 'hover:bg-muted',
            )}
            onClick={() => setRideType(option.type)}
          >
            <div className="flex items-center gap-3">
              <div
                className={cn(
                  'p-2 rounded-full',
                  rideType === option.type ? 'bg-ride-primary text-white' : 'bg-muted',
                )}
              >
                <Car className="h-5 w-5" />
              </div>
              <div>
                <h3 className="font-medium capitalize">{option.type}</h3>
                <p className="text-sm text-muted-foreground">{option.description}</p>
              </div>
            </div>
            <div className="text-right">
              <div className="font-medium">{formatPrice(option.amount)}</div>
              <div className="text-sm text-muted-foreground">{option.time}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default RideTypeSelector;
